import { useEffect, useRef, useState } from "react";
import { useNeural } from "@/store/neuralStore";
import { Gauge, Pause, Play, RotateCcw, Target, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

type Pos = { x: number; y: number };

const ZONES = [
  { name: "Task Zone",     x: 0.2, y: 0.2, color: "hsl(185 95% 55%)" },
  { name: "Tool Zone",     x: 0.8, y: 0.2, color: "hsl(270 90% 65%)" },
  { name: "Memory Zone",   x: 0.2, y: 0.8, color: "hsl(320 95% 60%)" },
  { name: "Conflict Zone", x: 0.8, y: 0.8, color: "hsl(0 90% 60%)" },
];

export function GameMode() {
  const agents = useNeural((s) => s.agents);
  const ref = useRef<HTMLCanvasElement>(null);
  const miniRef = useRef<HTMLCanvasElement>(null);
  const [paused, setPaused] = useState(false);
  const [speed, setSpeed] = useState(1);
  const stateRef = useRef<Record<string, { p: Pos; t: Pos; phase: number; status: string }>>({});

  // init state
  useEffect(() => {
    const s: typeof stateRef.current = {};
    agents.forEach((a) => {
      s[a.id] = {
        p: { x: a.x, y: a.y },
        t: { x: Math.random(), y: Math.random() },
        phase: Math.random() * Math.PI * 2,
        status: a.status,
      };
    });
    stateRef.current = s;
  }, [agents]);

  useEffect(() => {
    const c = ref.current!;
    const m = miniRef.current!;
    const ctx = c.getContext("2d")!;
    const mtx = m.getContext("2d")!;
    let raf = 0;
    let t = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const r = c.getBoundingClientRect();
      c.width = r.width * dpr;
      c.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const mr = m.getBoundingClientRect();
      m.width = mr.width * dpr;
      m.height = mr.height * dpr;
      mtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      const r = c.getBoundingClientRect();
      const w = r.width;
      const h = r.height;
      ctx.clearRect(0, 0, w, h);
      if (!paused) t += 0.016 * speed;

      // grid
      ctx.strokeStyle = "hsl(230 25% 16% / 0.6)";
      ctx.lineWidth = 1;
      for (let i = 0; i <= w; i += 32) {
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, h); ctx.stroke();
      }
      for (let i = 0; i <= h; i += 32) {
        ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(w, i); ctx.stroke();
      }

      // zones
      ZONES.forEach((z) => {
        const x = z.x * w, y = z.y * h;
        const g = ctx.createRadialGradient(x, y, 0, x, y, 140);
        g.addColorStop(0, z.color.replace(")", " / 0.25)").replace("hsl", "hsla"));
        g.addColorStop(1, z.color.replace(")", " / 0)").replace("hsl", "hsla"));
        ctx.fillStyle = g;
        ctx.fillRect(x - 140, y - 140, 280, 280);
        ctx.strokeStyle = z.color.replace(")", " / 0.5)").replace("hsl", "hsla");
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(x, y, 100, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = z.color;
        ctx.font = '600 11px "JetBrains Mono", monospace';
        ctx.textAlign = "center";
        ctx.fillText(z.name, x, y - 110);
      });

      // agents
      agents.forEach((a) => {
        const st = stateRef.current[a.id];
        if (!st) return;
        if (!paused && st.status === "active") {
          const dx = st.t.x - st.p.x;
          const dy = st.t.y - st.p.y;
          const d = Math.hypot(dx, dy);
          if (d < 0.02) st.t = { x: Math.random(), y: Math.random() };
          st.p.x += (dx / d) * 0.0015 * speed;
          st.p.y += (dy / d) * 0.0015 * speed;
          st.phase += 0.15 * speed;
        }
        const x = st.p.x * w;
        const y = st.p.y * h;
        const bob = Math.sin(st.phase) * 3;

        // shadow
        ctx.fillStyle = "hsl(230 35% 0% / 0.5)";
        ctx.beginPath();
        ctx.ellipse(x, y + 20, 14, 4, 0, 0, Math.PI * 2);
        ctx.fill();

        // body
        const grad = ctx.createRadialGradient(x, y - 4 + bob, 0, x, y + bob, 22);
        grad.addColorStop(0, a.color);
        grad.addColorStop(1, a.color.replace(/hsl\(([\d.]+)\s+([\d.]+%)\s+([\d.]+%)\)/, "hsla($1, $2, $3, 0)"));
        ctx.fillStyle = grad;
        ctx.fillRect(x - 24, y - 24 + bob, 48, 48);

        ctx.fillStyle = "hsl(230 35% 6%)";
        ctx.strokeStyle = a.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y + bob, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // emoji as character
        ctx.font = "16px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(a.emoji, x, y + bob);

        // label
        ctx.fillStyle = "hsl(200 20% 95%)";
        ctx.font = '600 10px "JetBrains Mono", monospace';
        ctx.textBaseline = "alphabetic";
        ctx.fillText(a.name, x, y + 32);

        // status
        const sc = st.status === "active" ? "hsl(145 80% 55%)" : st.status === "paused" ? "hsl(40 100% 60%)" : "hsl(220 15% 60%)";
        ctx.fillStyle = sc;
        ctx.beginPath();
        ctx.arc(x + 10, y - 10 + bob, 3, 0, Math.PI * 2);
        ctx.fill();
      });

      // mini map
      const mr = m.getBoundingClientRect();
      mtx.clearRect(0, 0, mr.width, mr.height);
      mtx.fillStyle = "hsl(230 30% 7%)";
      mtx.fillRect(0, 0, mr.width, mr.height);
      ZONES.forEach((z) => {
        mtx.fillStyle = z.color.replace(")", " / 0.6)").replace("hsl", "hsla");
        mtx.beginPath();
        mtx.arc(z.x * mr.width, z.y * mr.height, 14, 0, Math.PI * 2);
        mtx.fill();
      });
      agents.forEach((a) => {
        const st = stateRef.current[a.id];
        if (!st) return;
        mtx.fillStyle = a.color;
        mtx.beginPath();
        mtx.arc(st.p.x * mr.width, st.p.y * mr.height, 3, 0, Math.PI * 2);
        mtx.fill();
      });

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [agents, paused, speed]);

  return (
    <div className="relative h-full w-full overflow-hidden bg-grid bg-grid-fade">
      <canvas ref={ref} className="absolute inset-0 w-full h-full" />

      {/* HUD */}
      <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
        <div className="glass rounded-lg px-3 py-1.5 text-xs font-mono flex items-center gap-2">
          <Target className="w-3.5 h-3.5 text-primary" /> {agents.length} heroes deployed
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setPaused((p) => !p)}>
          {paused ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
          {paused ? "Resume" : "Pause"}
        </Button>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setSpeed((s) => (s === 1 ? 2 : s === 2 ? 4 : 1))}>
          <Gauge className="w-3 h-3" /> ×{speed}
        </Button>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => {
          const s = stateRef.current;
          Object.keys(s).forEach((k) => (s[k].t = { x: 0.5, y: 0.5 }));
        }}>
          <RotateCcw className="w-3 h-3" /> Recall All
        </Button>
        <Button size="sm" className="h-8 gap-1.5 bg-gradient-neural border-0 text-primary-foreground" onClick={() => {
          const s = stateRef.current;
          Object.keys(s).forEach((k) => (s[k].t = { x: Math.random(), y: Math.random() }));
        }}>
          <Zap className="w-3 h-3" /> Send Agents
        </Button>
      </div>

      {/* Mini map */}
      <div className="absolute bottom-3 right-3 z-10 glass rounded-lg p-1.5">
        <div className="text-[9px] font-mono text-muted-foreground mb-1 px-1">Mini map</div>
        <canvas ref={miniRef} className="w-[180px] h-[120px] rounded" />
      </div>
    </div>
  );
}
